// src/components/commonpage/BatchDetails.js
import { useState, useEffect, useCallback } from "react";
import Loader from "../utils/Loader";
import { useParams } from "react-router-dom";
import ApiServices from "../config/ApiServices";
import { toast } from "react-toastify";
import Schedule from "./Schedule";
import Trainer from "./Trainers";
import { useDispatch, useSelector } from "react-redux";
import { hideLoader, showLoader } from "../redux/actions/LoaderActions";
import {
  handleItemsPerPageChange,
  handlePageChange,
} from "../utils/PaginationUtilities";
import Students from "./Students";
import { BeatLoader } from "react-spinners";
import Tasks from "./Tasks";
import Material from "./Material";
import { showModal } from "../redux/actions/ModalActions";
import { isMoment } from "moment";

export default function BatchDetails({ title }) {
  const [batch, setBatch] = useState({});
  const [showItem, setShowItem] = useState(1);
  const { batchId } = useParams();
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [material, setMaterial] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageStudent, setCurrentPageStudent] = useState(0);
  const [currentPageTasks, setCurrentPageTasks] = useState(0);
  const [currentPageTrainer, setCurrentPageTrainer] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [itemsPerPageStudent, setItemsPerPageStudent] = useState(10);
  const [itemsPerPageTasks, setItemsPerPageTasks] = useState(10);
  const [itemsPerPageTrainer, setItemsPerPageTrainer] = useState(10);
  const [totalData, setTotalData] = useState(0);
  const [totalDataStudent, setTotalDataStudent] = useState(0);
  const [totalDataTasks, setTotalDataTasks] = useState(0);
  const [totalDataMaterials, setTotalDataMaterials] = useState(0);
  const [totalDataTrainers, setTotalDataTrainers] = useState(0);
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [loadingTrainer, setLoadingTrainer] = useState(true);
  const [status, setStatus] = useState(true);
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.loading);
  const isOpen = useSelector((state) => state.modal.isOpen);

  useEffect(() => {
    let isMounted = true;
    dispatch(showLoader());
    ApiServices.apiRequest("getSingleBatch", { _id: batchId })
      .then((res) => {
        if (isMounted && res.data.success) {
          setBatch(res.data.data);
          setTotalDataTrainers(res.data.data?.trainerId?.length || 0);
        } else if (isMounted) {
          toast.error(res.data.message, { toastId: "getSingleBatch" });
        }
      })
      .catch((err) => {
        if (isMounted) {
          toast.error(err?.message || "Something went wrong!", {
            toastId: "getSingleBatch",
          });
        }
      })
      .finally(() => {
        dispatch(hideLoader());
        setLoadingTrainer(false);
      });
    return () => {
      isMounted = false;
    };
  }, [batchId, isOpen, dispatch]);

  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    dispatch(showLoader());
    try {
      const res = await ApiServices.apiRequest("getSchedule", {
        batchId,
        limit: itemsPerPage,
        pageno: currentPage + 1,
      });
      if (res.data.success) {
        setTotalData(res.data.total || 0);
        setSchedules(res.data.data);
      } else {
        toast.error(res.data.message, { toastId: "getSchedule" });
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong", {
        toastId: "getSchedule",
      });
    } finally {
      dispatch(hideLoader());
    }
  }, [batchId, itemsPerPage, currentPage, dispatch]);

  useEffect(() => {
    if (!isOpen) fetchSchedules();
  }, [isOpen, currentPage, itemsPerPage, fetchSchedules]);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    setLoadingStudent(true);
    let isMounted = true;
    try {
      const res = await ApiServices.apiRequest("getAssignStudent", { batchId });
      if (isMounted && res.data.success) {
        let filterStatus = status === true || status === "true";
        const filtered = res.data.data
          .map((el) => ({
            ...el,
            students: el.students.filter(
              (e) => e.studentstatus === filterStatus
            ),
          }))
          .filter((el) => el.students.length > 0);
        setTotalDataStudent(
          filtered.reduce((acc, el) => acc + el.students.length, 0)
        );
        setStudents(filtered);
        console.log("students inside batches are:", filtered);
      } else if (isMounted) {
        toast.error(res.data.message, { toastId: "getAssignStudent" });
      }
    } catch (err) {
      if (isMounted) {
        toast.error(err?.message || "Something went wrong", {
          toastId: "getAssignStudent",
        });
      }
    } finally {
      if (isMounted) setLoadingStudent(false);
    }
    return () => {
      isMounted = false;
    };
  }, [batchId, status]);

  useEffect(() => {
    fetchStudents();
  }, [status, currentPageStudent, itemsPerPageStudent, fetchStudents]);
  useEffect(() => {
    if (!isOpen) {
      fetchStudents();
    }
  }, [isOpen, status, fetchStudents]);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    dispatch(showLoader());
    let isMounted = true;

    try {
      const res = await ApiServices.apiRequest("getTaskPagination", {
        batchId,
        limit: itemsPerPageTasks,
        pageno: currentPageTasks + 1,
        status,
      });

      if (isMounted && res.data.success) {
        setTotalDataTasks(res.data.total || 0);
        const activeTasks = (res.data.data || []).filter(
          (task) => task.status === true
        );

        setTasks(activeTasks);
      }
    } catch (err) {
      if (isMounted) {
        toast.error(err?.message || "Something went wrong", {
          toastId: "getTaskPagination",
        });
      }
    } finally {
      if (isMounted) dispatch(hideLoader());
    }

    return () => {
      isMounted = false;
    };
  }, [batchId, itemsPerPageTasks, currentPageTasks, status, dispatch]);

  useEffect(() => {
    fetchTasks();
  }, [currentPageTasks, itemsPerPageTasks, fetchTasks,  isOpen,currentPage,itemsPerPage,batchId  ]);

  // Fetch materials
  const fetchMaterials = useCallback(async () => {
    dispatch(showLoader());
    let isMounted = true;
    try {
      const res = await ApiServices.apiRequest("getMaterial", {
        batchId,
        limit: itemsPerPageTasks,
        pageno: currentPageTasks + 1,
        status,
      });
      if (isMounted && res.data.success) {
        setTotalDataMaterials(res.data.total || 0);
        setMaterial(res.data.data);
      }
    } catch (err) {
      if (isMounted)
        toast.error(err?.message || "Something went wrong", {
          toastId: "getMaterial",
        });
    } finally {
      if (isMounted) dispatch(hideLoader());
    }
    return () => {
      isMounted = false;
    };
  }, [batchId, itemsPerPageTasks, currentPageTasks, status, dispatch]);
  useEffect(() => {
    fetchMaterials();
  }, [currentPageTasks, itemsPerPageTasks, fetchMaterials,dispatch,isMoment, isOpen,currentPage,itemsPerPage,batchId  ]);
  // isOpen, currentPage, itemsPerPage, batchId

  // change status trainer (remove)
  const changeStatusTrainer = (trainerId) => {
    let data = {
      batchId: batchId,
      trainerId: trainerId,
      status: false,
    };
    dispatch(showModal("StatusModal", data, "removeTrainer", "removeTrainer"));
  };

  // change status student (remove/add back)

  const changeStatusStudent = (batchIdParam, studentId, currentStatus) => {
    const data = {
      _id: batchIdParam,
      studentId,
      studentstatus: !currentStatus,
      status: !currentStatus,
    };

    dispatch(
      showModal(
        "StatusModal",
        data,
        "changeStatusAssignStudent",
        currentStatus ? "removeStudent" : "addBackStudent",
        () => {
          setStudents((prev) =>
            prev
              .map((el) => ({
                ...el,
                students: el.students.filter((s) => {
                  if (s.studentId?._id === studentId) {
                    return !currentStatus;
                  }
                  return true;
                }),
              }))
              .filter((el) => el.students.length > 0)
          );

          setStatus(true);
          fetchStudents();
          setShowItem(5);
        }
      )
    );
  };

     const removeBulkPeople = () => {
    let data = {
      studentIds: JSON.stringify(selectedStudents),
      batchId: batchId,
    };
    dispatch(showModal("StatusModal", data, "bulkStudentRemove", "blockMulti"));
  };
  return (
    <div className="container">
      <div className="page-inner">
        {loading ? (
          <Loader />
        ) : (
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title text-capitalize">{`${batch?.name} (${batch?.batchTime})`}</h4>
                </div>
                <div className="card-body">
                  <ul
                    className="nav nav-tabs nav-line nav-color-secondary"
                    id="line-tab"
                    role="tablist"
                  >
                    <li className="nav-item">
                      <a
                        className={`nav-link ${showItem === 1 ? "active" : ""}`}
                        onClick={() => setShowItem(1)}
                        href="#line-schedule"
                      >
                        Schedule
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${showItem === 2 ? "active" : ""}`}
                        onClick={() => setShowItem(2)}
                        href="#line-material"
                      >
                        Material
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${showItem === 3 ? "active" : ""}`}
                        onClick={() => setShowItem(3)}
                        href="#line-tasks"
                      >
                        Tasks
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${showItem === 4 ? "active" : ""}`}
                        onClick={() => setShowItem(4)}
                        href="#line-trainers"
                      >
                        Trainers
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${showItem === 5 ? "active" : ""}`}
                        onClick={() => setShowItem(5)}
                        href="#line-students"
                      >
                        Students
                      </a>
                    </li>
                  </ul>
                  <div className="tab-content mt-3 mb-3" id="line-tabContent">
                    {/* Schedule */}
                    <div
                      className={`tab-pane fade ${
                        showItem === 1 ? "show active" : ""
                      }`}
                      id="line-schedule"
                    >
                      <Schedule
                        schedules={schedules}
                        totalData={totalData}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        handlePageChange={handlePageChange(setCurrentPage)}
                        handleItemsPerPageChange={handleItemsPerPageChange(
                          setItemsPerPage,
                          setCurrentPage
                        )}
                      />
                    </div>
                    {/* Material */}
                    <div
                      className={`tab-pane fade ${
                        showItem === 2 ? "show active" : ""
                      }`}
                      id="line-material"
                    >
                      <Material
                        material={material}
                        totalData={totalDataMaterials}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        handlePageChange={handlePageChange(setCurrentPage)}
                        batchId={batchId}
                      />
                    </div>
                    {/* Tasks */}
                    <div
                      className={`tab-pane fade ${
                        showItem === 3 ? "show active" : ""
                      }`}
                      id="line-tasks"
                    >
                      <Tasks
                        tasks={tasks}
                        totalData={totalDataTasks}
                        currentPage={currentPageTasks}
                        itemsPerPage={itemsPerPageTasks}
                        handlePageChange={handlePageChange(setCurrentPageTasks)}
                        batchId={batchId}
                      />
                    </div>
                    {/* Trainers */}
                    <div
                      className={`tab-pane fade ${
                        showItem === 4 ? "show active" : ""
                      }`}
                      id="line-trainers"
                    >
                      {loadingTrainer ? (
                        <div className="d-flex justify-content-center">
                          <BeatLoader
                            color="#1572e8"
                            size={15}
                            loading={loadingTrainer}
                          />
                        </div>
                      ) : (
                        <Trainer
                          peoples={batch}
                          totalData={totalDataTrainers}
                          currentPage={currentPageTrainer}
                          itemsPerPage={itemsPerPageTrainer}
                          handlePageChange={handlePageChange(
                            setCurrentPageTrainer
                          )}
                          handleItemsPerPageChange={handleItemsPerPageChange(
                            setItemsPerPageTrainer,
                            setCurrentPageTrainer
                          )}
                          selectedTrainers={selectedTrainers}
                          handleCheckboxChange={(id) => {
                            setSelectedTrainers((prev) => {
                              if (prev.includes(id))
                                return prev.filter((i) => i !== id);
                              else return [...prev, id];
                            });
                          }}
                          changeStatusTrainer={changeStatusTrainer}
                        />
                      )}
                    </div>
                    {/* Students */}
                    <div
                      className={`tab-pane fade ${
                        showItem === 5 ? "show active" : ""
                      }`}
                      id="line-students"
                    >
                     <div  className="d-flex justify-content-between">
                         <select
                        className="form-select mb-3 me-2 w-25"
                        onChange={(e) => {
                          setStatus(e.target.value === "true");
                          setShowItem(5);
                        }}
                        value={String(status)}
                      >
                        <option value="true">Current</option>
                        <option value="false">Removed</option>
                      </select>

                      <div>
                        <button
                      onClick={() => {
                        dispatch(showModal("AssignStudent"));
                      }}
                      className="btn btn-primary btn-round"
                    >
                      Add Student
                    </button>
                         {status ? (
                                            <div className="dropdown">
                                              <button
                                                className="btn dropdown-toggle"
                                                type="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                              >
                                                Actions
                                              </button>
                                              <ul className="dropdown-menu">
                                                <li>
                                                  <a
                                                    className="dropdown-item"
                                                    href="#"
                                                    onClick={removeBulkPeople}
                                                  >
                                                    Remove
                                                  </a>
                                                </li>
                                              </ul>
                                            </div>
                                          ) : (
                                            <div></div>
                                          )}
                      </div>
                     </div>


                      {loadingStudent ? (
                        <div className="d-flex justify-content-center">
                          <BeatLoader
                            color="#1572e8"
                            size={15}
                            loading={loadingStudent}
                          />
                          
                        </div>

                      ) : (
                        <Students
                          peoples={students}
                          totalData={totalDataStudent}
                          currentPage={currentPageStudent}
                          itemsPerPage={itemsPerPageStudent}
                          handlePageChange={handlePageChange(
                            setCurrentPageStudent
                          )}
                          handleItemsPerPageChange={handleItemsPerPageChange(
                            setItemsPerPageStudent,
                            setCurrentPageStudent
                          )}
                          status={status}
                          batchId={batchId}
                          changeStatusStudent={changeStatusStudent}
                          selectedStudents={selectedStudents}
                          handleCheckboxChange={(id) => {
                            setSelectedStudents((prev) => {
                              if (prev.includes(id))
                                return prev.filter((i) => i !== id);
                              else return [...prev, id];
                            });
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
